import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { EyeOff, RotateCw } from 'lucide-react';
import { adminQueries, getErrorMessage, keys } from '@blimo/api-client';
import { useApi, useMe } from '@blimo/api-client/react';
import { formatDateTime, hasPermission, labelOf, OUTBOUND_SOURCE_LABELS } from '@blimo/shared';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ConfirmDialog,
  DescriptionList,
  ErrorState,
  HtmlFrame,
  PageHeader,
  StatusBadge,
  toast,
} from '@blimo/ui';
import { DetailSkeleton } from '../../../components/data.jsx';
import { recipientText } from '../recipients.js';

export function OutboundDetailPage() {
  const api = useApi();
  const { data: me } = useMe();
  const queryClient = useQueryClient();
  const { messageId } = useParams({ strict: false });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { data: message, error, isPending, refetch } = useQuery(adminQueries.outboundMessage(api, messageId));

  const breadcrumb = (
    <Link to="/kommunikation/versandhistorie" className="hover:underline">
      Versandhistorie
    </Link>
  );
  if (error) {
    return (
      <>
        <PageHeader title="Nachricht" breadcrumb={breadcrumb} />
        <ErrorState error={error} onRetry={() => refetch()} notFoundTitle="Nachricht nicht gefunden" />
      </>
    );
  }
  if (isPending) return <DetailSkeleton />;

  const canResend = message.resendable && hasPermission(me, 'outbound-messages.resend');

  async function resend() {
    try {
      await api.admin.outboundMessages.resend(messageId);
      toast.success('Nachricht erneut gesendet');
      queryClient.invalidateQueries({ queryKey: keys.admin.resource('outbound-messages') });
    } catch (e) {
      toast.error(getErrorMessage(e, 'Die Nachricht konnte nicht erneut gesendet werden.'));
    }
  }

  const cc = message.recipients?.cc ?? [];
  const bcc = message.recipients?.bcc ?? [];

  return (
    <>
      <PageHeader
        breadcrumb={breadcrumb}
        title={message.subject || '(ohne Betreff)'}
        description={
          <span className="flex flex-wrap items-center gap-2">
            <StatusBadge domain="outboundMessage" status={message.status} />
            <span className="numeric">{formatDateTime(message.sent_at)}</span>
          </span>
        }
        actions={
          canResend && (
            <Button size="sm" variant="outline" onClick={() => setConfirmOpen(true)}>
              <RotateCw aria-hidden="true" /> Erneut senden
            </Button>
          )
        }
      />
      <div className="grid gap-3 lg:grid-cols-[22rem_1fr]">
        <Card>
          <CardHeader>
            <CardTitle as="h2">Versand</CardTitle>
          </CardHeader>
          <CardContent>
            <DescriptionList
              labelWidth="7rem"
              items={[
                { label: 'An', value: <span className="break-all">{recipientText(message.recipients)}</span> },
                { label: 'CC', value: cc.length ? <span className="break-all">{cc.join(', ')}</span> : null },
                { label: 'BCC', value: bcc.length ? <span className="break-all">{bcc.join(', ')}</span> : null },
                { label: 'Vorlage', value: message.template_key ? <span className="font-mono text-xs">{message.template_key}</span> : null },
                { label: 'Quelle', value: labelOf(OUTBOUND_SOURCE_LABELS, message.source) },
                { label: 'Kanal', value: message.channel },
                { label: 'Anhänge', value: message.attachments?.length ? message.attachments.join(', ') : null },
                {
                  label: 'Erneut von',
                  value: message.resent_from_id ? (
                    <Link
                      to="/kommunikation/versandhistorie/$messageId"
                      params={{ messageId: String(message.resent_from_id) }}
                      className="text-primary hover:underline"
                    >
                      Nachricht #{message.resent_from_id}
                    </Link>
                  ) : null,
                },
              ]}
            />
          </CardContent>
        </Card>
        <Card className="min-w-0">
          <CardHeader>
            <CardTitle as="h2">Inhalt</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {message.error && (
              <Alert variant="destructive">
                <AlertTitle>Fehler beim Versand</AlertTitle>
                <AlertDescription className="whitespace-pre-wrap break-words">{message.error}</AlertDescription>
              </Alert>
            )}
            {message.body_redacted || !message.body_html ? (
              <Alert variant="info">
                <EyeOff aria-hidden="true" />
                <div className="flex flex-col gap-1">
                  <AlertTitle>Inhalt nicht gespeichert</AlertTitle>
                  <AlertDescription>
                    {message.body_redacted
                      ? 'Der Inhalt dieser Nachricht wird aus Datenschutzgründen nicht gespeichert (z. B. Einladungs- oder Passwort-Links).'
                      : 'Für diese Nachricht liegt kein Inhalt vor.'}
                  </AlertDescription>
                </div>
              </Alert>
            ) : (
              <HtmlFrame html={message.body_html} title={`Inhalt der E-Mail „${message.subject ?? ''}“`} minHeight={320} />
            )}
          </CardContent>
        </Card>
      </div>
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Nachricht erneut senden?"
        description={`Die Nachricht wird mit unverändertem Inhalt erneut an ${recipientText(message.recipients)} gesendet.`}
        confirmLabel="Erneut senden"
        onConfirm={resend}
      />
    </>
  );
}
